import { useEffect, useState } from 'react'
import { X, AlertTriangle, Loader2, Recycle } from 'lucide-react'
import toast from 'react-hot-toast'
import { kioskApi } from './kiosk-api'
import KioskNumpad from './KioskNumpad'

type Motivo = { id: number; codigo: string; nome: string; categoria: string; reciclavel: boolean }

export default function KioskRefugo({ etapa_op_id, onClose, onDone }: {
  etapa_op_id: number; onClose: () => void; onDone: () => void
}) {
  const [motivos, setMotivos] = useState<Motivo[]>([])
  const [motivoId, setMotivoId] = useState<number | null>(null)
  const [campo, setCampo] = useState<'qtd' | 'peso'>('qtd')
  const [qtd, setQtd] = useState('')
  const [peso, setPeso] = useState('')
  const [obs, setObs] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    kioskApi.get<Motivo[]>('/kiosk/motivos-refugo').then(r => setMotivos(r.data))
  }, [])

  async function submit() {
    if (!motivoId) { toast.error('Selecione um motivo'); return }
    if (!qtd && !peso) { toast.error('Informe quantidade ou peso'); return }
    setLoading(true)
    try {
      await kioskApi.post('/kiosk/refugo', {
        etapa_op_id, motivo_refugo_id: motivoId,
        quantidade: parseFloat(qtd) || 0,
        peso_kg: parseFloat(peso) || 0,
        observacao: obs || undefined,
      })
      toast.success('Refugo registrado')
      onDone()
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro')
    } finally { setLoading(false) }
  }

  const valorAtivo = campo === 'qtd' ? qtd : peso
  const setAtivo = campo === 'qtd' ? setQtd : setPeso

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center">
      <div className="bg-white w-full md:max-w-lg rounded-t-2xl md:rounded-2xl p-5 space-y-4 max-h-[95vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <h2 className="text-xl font-bold flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" /> Registrar refugo
          </h2>
          <button onClick={onClose}><X className="h-6 w-6" /></button>
        </div>

        <div>
          <div className="text-xs uppercase text-ink-500 font-semibold mb-2">Motivo</div>
          <div className="grid grid-cols-2 gap-2">
            {motivos.map(m => (
              <button
                key={m.id}
                onClick={() => setMotivoId(m.id)}
                className={`p-3 rounded-xl text-left text-sm font-medium border-2 transition ${
                  motivoId === m.id ? 'border-red-500 bg-red-50' : 'border-ink-200 hover:border-ink-300'
                }`}
              >
                <div className="flex items-center gap-1">
                  {m.nome}
                  {m.reciclavel && <Recycle className="h-3 w-3 text-emerald-600" />}
                </div>
                <div className="text-xs text-ink-500 mt-0.5">{m.categoria}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setCampo('qtd')}
            className={`h-14 rounded-xl font-semibold ${campo === 'qtd' ? 'bg-red-600 text-white' : 'bg-ink-100'}`}
          >
            <div className="text-xs uppercase opacity-80">Quantidade</div>
            <div className="text-2xl">{qtd || '0'}</div>
          </button>
          <button
            onClick={() => setCampo('peso')}
            className={`h-14 rounded-xl font-semibold ${campo === 'peso' ? 'bg-red-600 text-white' : 'bg-ink-100'}`}
          >
            <div className="text-xs uppercase opacity-80">Peso (kg)</div>
            <div className="text-2xl">{peso || '0'}</div>
          </button>
        </div>

        <KioskNumpad value={valorAtivo} onChange={setAtivo} allowDecimal maxLen={9} />

        <input
          value={obs}
          onChange={(e) => setObs(e.target.value)}
          placeholder="Observação (opcional)"
          className="w-full h-12 px-4 border-2 border-ink-200 rounded-xl"
        />

        <button
          onClick={submit}
          disabled={!motivoId || loading || (!qtd && !peso)}
          className="w-full h-16 bg-red-600 hover:bg-red-700 text-white font-bold text-lg rounded-xl flex items-center justify-center gap-2 disabled:opacity-40"
        >
          {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : 'Registrar refugo'}
        </button>
      </div>
    </div>
  )
}
